// /docs layout: top header (matches Shell visually, doesn't gate auth so
// signed-out visitors can read too) + sticky sidebar with section nav + the
// active sub-page rendered in <Outlet />.

import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { getJwt } from "../../lib/api";
import { MBWordmark } from "../../components/mb";

export type DocSection = {
  slug: string;
  label: string;
  blurb: string;
};

// Authoritative ordering — used by the sidebar + the index tile grid + the
// per-page prev/next footer.
export const DOC_SECTIONS: DocSection[] = [
  { slug: "quickstart", label: "Quickstart", blurb: "Build your first project + widget end-to-end in 5 minutes." },
  { slug: "endpoints", label: "Workshop vs project endpoints", blurb: "The two MCP URLs and which one to connect when." },
  { slug: "widgets", label: "Building widgets", blurb: "How dynamic_widget works, projectName auto-save, theme + error visibility." },
  { slug: "data-tools", label: "Data tools + callTool()", blurb: "The button → data tool → refresh pattern, full Orders example." },
  { slug: "triggers", label: "Trigger phrases & natural demos", blurb: "Make a chat client pick your tool from natural phrasing." },
  { slug: "live-data", label: "Live data, secrets & fetchData", blurb: "Allowlists + KMS-encrypted secrets + a real API walkthrough." },
  { slug: "sharing", label: "Sharing & teams", blurb: "Public links + invite teammates by email (read / write)." },
  { slug: "remixing", label: "Remixing & forking", blurb: "Clone a whole project to re-theme it, or lift one widget into another." },
  { slug: "troubleshooting", label: "Troubleshooting", blurb: "Blank widgets, OAuth errors, missing tools — every confusion we've hit." },
];

export function DocsLayout() {
  const loc = useLocation();
  const isIndex = loc.pathname === "/docs" || loc.pathname === "/docs/";

  return (
    <div style={{ minHeight: "100%", display: "flex", flexDirection: "column", background: "var(--bg-0)" }}>
      <DocsHeader />
      {isIndex ? (
        <Outlet />
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "260px 1fr",
            gap: 0,
            maxWidth: 1320,
            width: "100%",
            margin: "0 auto",
          }}
          className="mb-docs-grid"
        >
          <DocsSidebar />
          <div style={{ minWidth: 0 }}>
            <Outlet />
          </div>
        </div>
      )}
    </div>
  );
}

function DocsHeader() {
  const signedIn = !!getJwt();
  return (
    <header
      style={{
        position: "sticky",
        top: 0,
        zIndex: 50,
        height: 56,
        borderBottom: "1px solid var(--line-2)",
        background: "rgba(7,9,12,0.92)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
      }}
    >
      <div
        style={{
          maxWidth: 1320,
          margin: "0 auto",
          height: "100%",
          padding: "0 28px",
          display: "flex",
          alignItems: "center",
          gap: 20,
        }}
      >
        <Link to="/" style={{ display: "inline-flex", alignItems: "center" }}>
          <MBWordmark size={17} />
        </Link>
        <span
          style={{
            padding: "3px 8px",
            background: "var(--accent-soft)",
            color: "var(--accent-fg)",
            border: "1px solid var(--accent-line)",
            borderRadius: 999,
            fontFamily: "var(--font-mono)",
            fontSize: 10.5,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          Docs
        </span>
        <nav style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <Link
            to="/docs"
            style={{
              padding: "6px 12px",
              borderRadius: 6,
              color: "var(--fg-2)",
              fontFamily: "var(--font-sans)",
              fontSize: 14,
              fontWeight: 500,
              textDecoration: "none",
            }}
          >
            All docs
          </Link>
        </nav>
        <div style={{ marginLeft: "auto", display: "flex", gap: 10, alignItems: "center" }}>
          {signedIn ? (
            <>
              <Link
                to="/projects"
                style={{
                  padding: "6px 12px",
                  borderRadius: 6,
                  color: "var(--fg-2)",
                  fontFamily: "var(--font-sans)",
                  fontSize: 14,
                  fontWeight: 500,
                  textDecoration: "none",
                  border: "1px solid var(--line-2)",
                  background: "var(--surface-1)",
                }}
              >
                Back to projects
              </Link>
              <Link
                to="/settings"
                style={{
                  padding: "6px 12px",
                  borderRadius: 6,
                  color: "var(--fg-3)",
                  fontFamily: "var(--font-sans)",
                  fontSize: 14,
                  textDecoration: "none",
                }}
              >
                Settings
              </Link>
            </>
          ) : (
            <Link
              to="/login"
              style={{
                padding: "6px 14px",
                borderRadius: 6,
                color: "#fff",
                background: "var(--accent)",
                fontFamily: "var(--font-sans)",
                fontSize: 14,
                fontWeight: 500,
                textDecoration: "none",
              }}
            >
              Sign in
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}

function DocsSidebar() {
  return (
    <aside
      style={{
        borderRight: "1px solid var(--line-1)",
        padding: "40px 20px",
        position: "sticky",
        top: 56,
        alignSelf: "start",
        height: "calc(100vh - 56px)",
        overflowY: "auto",
      }}
      className="mb-docs-sidebar"
    >
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 10.5,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "var(--fg-4)",
          marginBottom: 14,
          padding: "0 8px",
        }}
      >
        Guides
      </div>
      <nav style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {DOC_SECTIONS.map((s) => (
          <NavLink
            key={s.slug}
            to={`/docs/${s.slug}`}
            end
            style={({ isActive }) => ({
              display: "block",
              padding: "8px 12px",
              borderRadius: 6,
              fontFamily: "var(--font-sans)",
              fontSize: 13.5,
              fontWeight: isActive ? 600 : 400,
              color: isActive ? "var(--fg-0)" : "var(--fg-2)",
              background: isActive ? "var(--surface-2)" : "transparent",
              boxShadow: isActive ? "inset 3px 0 0 var(--accent)" : "none",
              textDecoration: "none",
              lineHeight: "20px",
            })}
          >
            {s.label}
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}
